import { useState, useRef, useCallback, useEffect, useMemo } from 'react';

interface GpsPoint {
    x: number;  // 0-1 normalized (lng)
    y: number;  // 0-1 normalized (lat)
    v: number;  // velocity mph
    hr: number; // heart rate bpm
    ts: number; // unix timestamp
}

type HeatMode = 'time' | 'speed' | 'hr';

interface CanvasOverlayStats {
    distance: string;
    playerLoad: string;
    topSpeed: string;
    maxHR: string;
}

interface FieldHeatMapProps {
    points: GpsPoint[];
    athleteName?: string;
    sessionLabel?: string;
    stats?: CanvasOverlayStats;
    /** When true, draws name + stats overlay on the canvas (set during export) */
    showOverlay?: boolean;
    /** Externally controlled heat mode (if provided, internal tabs are hidden) */
    mode?: HeatMode;
    onModeChange?: (mode: HeatMode) => void;
    /** Dark mode — adapts field bg/line colors */
    isDark?: boolean;
}

// Standard soccer pitch ~105×68m → aspect ratio
const PITCH_RATIO = 68 / 105; // height / width ≈ 0.648

// Grid resolution for heat computation — higher = smoother
const GRID_COLS = 105;
const GRID_ROWS = Math.round(GRID_COLS * PITCH_RATIO); // ~68

// ── Color gradient helpers ──────────────────────────────────────────

const PALETTE_DARK = [
    [0, 0, 0, 0],        // transparent
    [0, 0, 255, 0.35],   // blue
    [0, 200, 100, 0.5],  // teal-green
    [255, 255, 0, 0.6],  // yellow
    [255, 80, 0, 0.75],  // orange
    [255, 0, 0, 0.85],   // red
];

const PALETTE_LIGHT = [
    [0, 0, 0, 0],        // transparent
    [0, 0, 255, 0.55],   // blue — boosted
    [0, 180, 80, 0.7],   // teal-green — boosted + darker
    [230, 220, 0, 0.8],  // yellow — boosted + slightly darker
    [240, 60, 0, 0.88],  // orange — boosted
    [220, 0, 0, 0.95],   // red — near opaque
];

function valueToColor(t: number, isDark = true): [number, number, number, number] {
    const clamped = Math.max(0, Math.min(1, t));
    const palette = isDark ? PALETTE_DARK : PALETTE_LIGHT;
    const idx = clamped * (palette.length - 1);
    const lo = Math.floor(idx);
    const hi = Math.min(lo + 1, palette.length - 1);
    const frac = idx - lo;
    return [
        Math.round(palette[lo][0] + (palette[hi][0] - palette[lo][0]) * frac),
        Math.round(palette[lo][1] + (palette[hi][1] - palette[lo][1]) * frac),
        Math.round(palette[lo][2] + (palette[hi][2] - palette[lo][2]) * frac),
        palette[lo][3] + (palette[hi][3] - palette[lo][3]) * frac,
    ];
}

// ── Grid computation ────────────────────────────────────────────────

function buildGrid(points: GpsPoint[], mode: HeatMode): { grid: number[][]; maxRaw: number; minRaw: number } {
    const grid: number[][] = Array.from({ length: GRID_ROWS }, () => Array(GRID_COLS).fill(0));
    const counts: number[][] = Array.from({ length: GRID_ROWS }, () => Array(GRID_COLS).fill(0));

    for (const p of points) {
        const col = Math.min(GRID_COLS - 1, Math.max(0, Math.floor(p.x * GRID_COLS)));
        const row = Math.min(GRID_ROWS - 1, Math.max(0, Math.floor(p.y * GRID_ROWS)));

        if (mode === 'time') {
            grid[row][col] += 1;
        } else if (mode === 'speed') {
            grid[row][col] += p.v;
            counts[row][col] += 1;
        } else {
            if (p.hr > 0) {
                grid[row][col] += p.hr;
                counts[row][col] += 1;
            }
        }
    }

    // Average for speed/hr modes
    if (mode !== 'time') {
        for (let r = 0; r < GRID_ROWS; r++) {
            for (let c = 0; c < GRID_COLS; c++) {
                if (counts[r][c] > 0) {
                    grid[r][c] /= counts[r][c];
                }
            }
        }
    }

    // Gaussian blur (2 passes of 3×3 averaging)
    const blur = (src: number[][]): number[][] => {
        const dst: number[][] = Array.from({ length: GRID_ROWS }, () => Array(GRID_COLS).fill(0));
        for (let r = 0; r < GRID_ROWS; r++) {
            for (let c = 0; c < GRID_COLS; c++) {
                let sum = 0;
                let cnt = 0;
                for (let dr = -1; dr <= 1; dr++) {
                    for (let dc = -1; dc <= 1; dc++) {
                        const nr = r + dr;
                        const nc = c + dc;
                        if (nr >= 0 && nr < GRID_ROWS && nc >= 0 && nc < GRID_COLS) {
                            const weight = (dr === 0 && dc === 0) ? 4 : 1;
                            sum += src[nr][nc] * weight;
                            cnt += weight;
                        }
                    }
                }
                dst[r][c] = sum / cnt;
            }
        }
        return dst;
    };

    let blurred = grid;
    for (let i = 0; i < 5; i++) blurred = blur(blurred);

    // Find min/max for normalization
    let maxVal = 0;
    let minVal = Infinity;
    for (let r = 0; r < GRID_ROWS; r++) {
        for (let c = 0; c < GRID_COLS; c++) {
            if (blurred[r][c] > 0) {
                maxVal = Math.max(maxVal, blurred[r][c]);
                minVal = Math.min(minVal, blurred[r][c]);
            }
        }
    }
    if (minVal === Infinity) minVal = 0;

    // Normalize to 0-1
    const range = maxVal - minVal || 1;
    for (let r = 0; r < GRID_ROWS; r++) {
        for (let c = 0; c < GRID_COLS; c++) {
            if (blurred[r][c] > 0) {
                blurred[r][c] = (blurred[r][c] - minVal) / range;
            }
        }
    }

    return { grid: blurred, maxRaw: maxVal, minRaw: minVal };
}

// ── Stats grid for tooltip (all metrics in one pass) ────────────────

interface CellStats {
    count: number;
    totalSpeed: number;
    speedSamples: number;
    totalHr: number;
    hrSamples: number;
}

function buildStatsGrid(points: GpsPoint[]): CellStats[][] {
    const grid: CellStats[][] = Array.from({ length: GRID_ROWS }, () =>
        Array.from({ length: GRID_COLS }, () => ({ count: 0, totalSpeed: 0, speedSamples: 0, totalHr: 0, hrSamples: 0 }))
    );

    for (const p of points) {
        const col = Math.min(GRID_COLS - 1, Math.max(0, Math.floor(p.x * GRID_COLS)));
        const row = Math.min(GRID_ROWS - 1, Math.max(0, Math.floor(p.y * GRID_ROWS)));
        const cell = grid[row][col];
        cell.count += 1;
        if (p.v > 0) { cell.totalSpeed += p.v; cell.speedSamples += 1; }
        if (p.hr > 0) { cell.totalHr += p.hr; cell.hrSamples += 1; }
    }

    return grid;
}

// ── Pitch drawing ───────────────────────────────────────────────────

function drawPitchBackground(ctx: CanvasRenderingContext2D, w: number, h: number, isDark: boolean) {
    ctx.fillStyle = isDark ? '#1a1a1a' : '#fafafa';
    ctx.fillRect(0, 0, w, h);
}

function drawPitchLines(ctx: CanvasRenderingContext2D, w: number, h: number, isDark: boolean) {
    ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)';
    ctx.lineWidth = 1.5;

    const pad = 8;
    const fw = w - pad * 2; // field width
    const fh = h - pad * 2; // field height
    const fx = pad;
    const fy = pad;

    // Outer boundary
    ctx.strokeRect(fx, fy, fw, fh);

    // Halfway line
    ctx.beginPath();
    ctx.moveTo(fx + fw / 2, fy);
    ctx.lineTo(fx + fw / 2, fy + fh);
    ctx.stroke();

    // Center circle (radius ~9.15m on 105m pitch ≈ 8.7% of width)
    const centerR = fw * 0.087;
    ctx.beginPath();
    ctx.arc(fx + fw / 2, fy + fh / 2, centerR, 0, Math.PI * 2);
    ctx.stroke();

    // Center spot
    ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)';
    ctx.beginPath();
    ctx.arc(fx + fw / 2, fy + fh / 2, 2, 0, Math.PI * 2);
    ctx.fill();

    // Penalty areas (16.5m from goal line, 40.3m wide → 15.7% depth, 38.4% of height)
    const paW = fw * 0.157; // depth from each end
    const paH = fh * 0.384; // width of penalty area
    const paY = fy + (fh - paH) / 2;
    // Left
    ctx.strokeRect(fx, paY, paW, paH);
    // Right
    ctx.strokeRect(fx + fw - paW, paY, paW, paH);

    // Goal areas (5.5m from goal line, 18.3m wide → 5.2% depth, 17.4% of height)
    const gaW = fw * 0.052;
    const gaH = fh * 0.174;
    const gaY = fy + (fh - gaH) / 2;
    // Left
    ctx.strokeRect(fx, gaY, gaW, gaH);
    // Right
    ctx.strokeRect(fx + fw - gaW, gaY, gaW, gaH);

    // Penalty spots (11m from goal → 10.5% of width)
    const penX = fw * 0.105;
    ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)';
    ctx.beginPath();
    ctx.arc(fx + penX, fy + fh / 2, 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(fx + fw - penX, fy + fh / 2, 2, 0, Math.PI * 2);
    ctx.fill();

    // Penalty arcs
    const penArcR = centerR;
    ctx.beginPath();
    ctx.arc(fx + penX, fy + fh / 2, penArcR, -0.65, 0.65);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(fx + fw - penX, fy + fh / 2, penArcR, Math.PI - 0.65, Math.PI + 0.65);
    ctx.stroke();

    // Corner arcs
    const cornerR = 4;
    // Top-left
    ctx.beginPath();
    ctx.arc(fx, fy, cornerR, 0, Math.PI / 2);
    ctx.stroke();
    // Top-right
    ctx.beginPath();
    ctx.arc(fx + fw, fy, cornerR, Math.PI / 2, Math.PI);
    ctx.stroke();
    // Bottom-left
    ctx.beginPath();
    ctx.arc(fx, fy + fh, cornerR, -Math.PI / 2, 0);
    ctx.stroke();
    // Bottom-right
    ctx.beginPath();
    ctx.arc(fx + fw, fy + fh, cornerR, Math.PI, Math.PI * 1.5);
    ctx.stroke();
}

// ── Legend drawing ───────────────────────────────────────────────────

function drawLegend(ctx: CanvasRenderingContext2D, w: number, h: number, _mode: HeatMode, _minRaw: number, _maxRaw: number, isDark: boolean) {
    const barW = 100;
    const barH = 10;
    const radius = 4;

    ctx.font = '600 9px Inter, sans-serif';
    ctx.textBaseline = 'middle';

    const lessW = ctx.measureText('Less').width;
    const moreW = ctx.measureText('More').width;
    const fullW = lessW + 6 + barW + 6 + moreW;
    const startX = w - fullW - 14;
    const y = h - barH - 10;

    // "Less" label
    const labelColor = isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)';
    ctx.fillStyle = labelColor;
    ctx.textAlign = 'left';
    ctx.fillText('Less', startX, y + barH / 2);

    // Gradient bar — draw inside a clipped rounded rect
    const barX = startX + lessW + 6;
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(barX, y, barW, barH, radius);
    ctx.clip();

    for (let i = 0; i < barW; i++) {
        const t = i / (barW - 1);
        const [r, g, b, a] = valueToColor(t, isDark);
        ctx.fillStyle = `rgba(${r},${g},${b},${Math.max(a, 0.4)})`;
        ctx.fillRect(barX + i, y, 1, barH);
    }

    // Apply grain within the clipped region
    const dpr = window.devicePixelRatio || 2;
    const px = Math.round(barX * dpr);
    const py = Math.round(y * dpr);
    const pw = Math.round(barW * dpr);
    const ph = Math.round(barH * dpr);
    const imgData = ctx.getImageData(px, py, pw, ph);
    const data = imgData.data;
    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 5) continue;
        const noise = (Math.random() - 0.5) * 0.15 * 255;
        data[i] = Math.max(0, Math.min(255, data[i] + noise));
        data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + noise));
        data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + noise));
    }
    ctx.putImageData(imgData, px, py);
    ctx.restore();

    // "More" label
    ctx.fillStyle = labelColor;
    ctx.textAlign = 'left';
    ctx.fillText('More', barX + barW + 6, y + barH / 2);
}

// ── Overlay text (name + stats) ─────────────────────────────────────

function drawOverlayText(ctx: CanvasRenderingContext2D, w: number, athleteName?: string, sessionLabel?: string, stats?: CanvasOverlayStats) {
    if (!athleteName && !stats) return;

    // Top-left: athlete name + session date
    if (athleteName) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
        ctx.font = '700 13px Inter, system-ui, sans-serif';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillText(athleteName, 14, 10);

        if (sessionLabel) {
            ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
            ctx.font = '400 8px Inter, system-ui, sans-serif';
            ctx.fillText(sessionLabel, 14, 26);
        }
    }

    // Top-right: stats in a row, right-aligned to match left margin (14px)
    if (stats) {
        const items = [
            { label: 'MAX HR', value: stats.maxHR },
            { label: 'TOP SPEED', value: stats.topSpeed },
            { label: 'PLAYER LOAD', value: stats.playerLoad },
            { label: 'DISTANCE', value: stats.distance },
        ];

        ctx.textBaseline = 'top';
        const gap = 72;
        let x = w - 14; // same margin as name on the left

        for (const item of items) {
            // Value (right-aligned)
            ctx.textAlign = 'right';
            ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
            ctx.font = '700 14px Inter, system-ui, sans-serif';
            ctx.fillText(item.value, x, 21);

            // Label (right-aligned above value)
            ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
            ctx.font = '500 7px Inter, system-ui, sans-serif';
            ctx.fillText(item.label, x, 10);

            x -= gap;
        }
    }
}

// ── Grain effect ────────────────────────────────────────────────────

function applyGrain(ctx: CanvasRenderingContext2D, w: number, h: number, intensity: number = 0.18) {
    const imageData = ctx.getImageData(0, 0, w, h);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
        // Only grain non-transparent pixels (heat data)
        if (data[i + 3] < 5) continue;

        const noise = (Math.random() - 0.5) * 2 * intensity * 255;
        data[i]     = Math.max(0, Math.min(255, data[i] + noise));
        data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + noise));
        data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + noise));
    }
    ctx.putImageData(imageData, 0, 0);
}

// ── Main component ──────────────────────────────────────────────────

export default function FieldHeatMap({ points, athleteName, sessionLabel, stats, showOverlay = false, mode: externalMode, onModeChange, isDark = false }: FieldHeatMapProps) {
    const [internalMode, setInternalMode] = useState<HeatMode>('time');
    const mode = externalMode ?? internalMode;
    const setMode = onModeChange ?? setInternalMode;
    const showTabs = !externalMode;
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [hoveredCell, setHoveredCell] = useState<{ row: number; col: number; x: number; y: number } | null>(null);

    // Pre-compute stats grid (all metrics) once when points change
    const statsGrid = useMemo(() => buildStatsGrid(points), [points]);

    // Layout refs so mousemove can access without re-render
    const layoutRef = useRef({ pad: 8, fw: 0, fh: 0, cellW: 0, cellH: 0 });

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container || points.length === 0) return;

        const rect = container.getBoundingClientRect();
        const w = rect.width;
        const h = Math.round(w * PITCH_RATIO);
        const dpr = 2;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;

        const ctx = canvas.getContext('2d')!;
        ctx.scale(dpr, dpr);

        const pixW = w * dpr;
        const pixH = h * dpr;

        // 1. Pitch background + first pass of lines
        drawPitchBackground(ctx, w, h, isDark);
        drawPitchLines(ctx, w, h, isDark);

        // 2. Build heat grid
        const { grid, maxRaw, minRaw } = buildGrid(points, mode);

        // 3. Render heat cells onto an offscreen canvas (so grain only affects heat)
        const offscreen = document.createElement('canvas');
        offscreen.width = pixW;
        offscreen.height = pixH;
        const offCtx = offscreen.getContext('2d')!;
        offCtx.scale(dpr, dpr);

        const pad = 8;
        const fw = w - pad * 2;
        const fh = h - pad * 2;
        const cellW = fw / GRID_COLS;
        const cellH = fh / GRID_ROWS;
        layoutRef.current = { pad, fw, fh, cellW, cellH };

        for (let r = 0; r < GRID_ROWS; r++) {
            for (let c = 0; c < GRID_COLS; c++) {
                const val = grid[r][c];
                if (val <= 0.01) continue;

                const [red, green, blue, alpha] = valueToColor(val, isDark);
                offCtx.fillStyle = `rgba(${red},${green},${blue},${alpha})`;
                const cx = pad + c * cellW;
                const cy = pad + (GRID_ROWS - 1 - r) * cellH;
                offCtx.fillRect(cx, cy, cellW + 0.5, cellH + 0.5);
            }
        }

        // 4. Apply film grain to the offscreen heat layer
        offCtx.setTransform(1, 0, 0, 1, 0, 0);
        applyGrain(offCtx, pixW, pixH);

        // 5. Composite grainy heat layer onto main canvas
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.drawImage(offscreen, 0, 0);
        ctx.scale(dpr, dpr);

        // 6. Redraw pitch lines on top (clean, no grain)
        drawPitchLines(ctx, w, h, isDark);

        // 7. Legend (crisp, after grain)
        drawLegend(ctx, w, h, mode, minRaw, maxRaw, isDark);
        if (showOverlay) {
            drawOverlayText(ctx, w, athleteName, sessionLabel, stats);
        }
    }, [points, mode, athleteName, sessionLabel, stats, showOverlay, isDark]);

    useEffect(() => {
        // Use rAF to ensure container has been laid out before measuring
        const frame = requestAnimationFrame(() => draw());
        window.addEventListener('resize', draw);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', draw);
        };
    }, [draw]);

    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;
        const { pad, cellW, cellH } = layoutRef.current;

        if (cellW <= 0 || cellH <= 0) return;

        const col = Math.floor((mx - pad) / cellW);
        const row = GRID_ROWS - 1 - Math.floor((my - pad) / cellH);

        if (row < 0 || row >= GRID_ROWS || col < 0 || col >= GRID_COLS) {
            setHoveredCell(null);
            return;
        }

        setHoveredCell({ row, col, x: e.clientX, y: e.clientY });
    }, [statsGrid]);

    const handleMouseLeave = useCallback(() => setHoveredCell(null), []);

    // Tooltip data — aggregate a 3×3 neighborhood to match the blurred visual
    const tooltipData = useMemo(() => {
        if (!hoveredCell) return null;
        const { row, col } = hoveredCell;
        const agg: CellStats = { count: 0, totalSpeed: 0, speedSamples: 0, totalHr: 0, hrSamples: 0 };
        for (let dr = -1; dr <= 1; dr++) {
            for (let dc = -1; dc <= 1; dc++) {
                const r = row + dr;
                const c = col + dc;
                if (r >= 0 && r < GRID_ROWS && c >= 0 && c < GRID_COLS) {
                    const cell = statsGrid[r][c];
                    agg.count += cell.count;
                    agg.totalSpeed += cell.totalSpeed;
                    agg.speedSamples += cell.speedSamples;
                    agg.totalHr += cell.totalHr;
                    agg.hrSamples += cell.hrSamples;
                }
            }
        }
        return agg.count > 0 ? agg : null;
    }, [hoveredCell, statsGrid]);

    const modes: { id: HeatMode; label: string }[] = [
        { id: 'time', label: 'Time' },
        { id: 'speed', label: 'Speed' },
        { id: 'hr', label: 'HR' },
    ];

    return (
        <div>
            {/* Mode toggle — hidden when mode is controlled externally */}
            {showTabs && (
            <div className="flex items-center gap-0 mb-3">
                {modes.map((m) => (
                    <button
                        key={m.id}
                        onClick={() => setMode(m.id)}
                        className={`px-3 py-1 text-xs font-medium transition-all cursor-pointer ${
                            mode === m.id ? 'cgm-tab-active' : 'cgm-tab'
                        }`}
                    >
                        {m.label}
                    </button>
                ))}
            </div>
            )}

            {/* Canvas */}
            <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
                <canvas
                    ref={canvasRef}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    style={{ cursor: tooltipData ? 'crosshair' : 'default' }}
                />

                {/* Tooltip overlay */}
                {hoveredCell && tooltipData && tooltipData.count > 0 && (
                    <div
                        className="fixed z-50 px-3 py-2 text-xs pointer-events-none cgm-tooltip border"
                        style={{
                            left: hoveredCell.x + 12,
                            top: hoveredCell.y - 8,
                            borderColor: 'var(--card-border)',
                        }}
                    >
                        <div className="font-semibold cgm-text-primary mb-1">Zone Stats</div>
                        <div className="space-y-0.5 cgm-text-secondary">
                            <div>Time: {tooltipData.count} pts ({(tooltipData.count / points.length * 100).toFixed(1)}%)</div>
                            {tooltipData.speedSamples > 0 && (
                                <div>Avg Speed: {(tooltipData.totalSpeed / tooltipData.speedSamples).toFixed(1)} mph</div>
                            )}
                            {tooltipData.hrSamples > 0 && (
                                <div>Avg HR: {Math.round(tooltipData.totalHr / tooltipData.hrSamples)} bpm</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
